import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Button, Flex, Text, useToast } from "@chakra-ui/react";
import Card from "../../components/Card/Card";
import { AgGridReact } from "ag-grid-react";
import { agGridStyles } from "../../components/Styles/AgGridStyles.js";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";

const customAgGridStyles = `
  ${agGridStyles}
  .ag-theme-alpine {
    --ag-grid-size: 3px;
    --ag-border-color: #E2E8F0;
    --ag-row-hover-color: #F7FAFC;
    --ag-selected-row-background-color: #EDF2F7;
  }
  .ag-theme-alpine .ag-cell {
    text-align: left !important;
    padding: 8px;
  }
  .ag-theme-alpine .ag-row {
    text-align: left !important;
  }
  .ag-theme-alpine .ag-row.ag-row-selected {
    background-color: #EDF2F7 !important;
  }
  .ag-theme-alpine .ag-row.ag-row-editing {
    border: 1px solid #E2E8F0 !important;
  }
  .ag-theme-alpine .ag-row.ag-row-editing .ag-cell {
    border: 1px solid #E2E8F0 !important;
  }
  .ag-theme-alpine .ag-row.ag-row-editing .ag-cell-not-inline-editing {
    border: 1px solid #E2E8F0 !important;
  }
  .ag-theme-alpine .ag-row.ag-row-editing .ag-cell-inline-editing {
    border: 1px solid #E2E8F0 !important;
  }
`;

const ActiveProductScreen = () => {
  const [inactiveProducts, setInactiveProducts] = useState([]);
  const [activeProducts, setActiveProducts] = useState([]);
  const [changedProducts, setChangedProducts] = useState([]);
  const gridRefInactive = useRef();
  const gridRefActive = useRef();
  const toast = useToast();

  // Fetch active and inactive products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const activeResponse = await apiClient.get(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STATUS.ACTIVE}`
        );
        setActiveProducts(
          activeResponse.data.map((product) => ({
            id: product.classId,
            name: product.productName,
            active: true,
          }))
        );

        const inactiveResponse = await apiClient.get(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STATUS.INACTIVE}`
        );
        setInactiveProducts(
          inactiveResponse.data.map((product) => ({
            id: product.classId,
            name: product.productName,
            active: false,
          }))
        );
      } catch (error) {
        console.error("Error fetching products:", error);
        toast({
          title: "Error",
          description: "Failed to fetch products. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchProducts();
  }, [toast]);

  // Update product status on the server
  const updateProductStatus = async (classIds, newStatus) => {
    try {
      const response = await apiClient.put(
        `https://orgi-api.sconexsoft.com/orgi/status/update`,
        {
          classIdList: classIds,
          newStatus,
        }
      );
      console.log(`API Response for newStatus=${newStatus}:`, response.data); // Debug log

      // Handle different response formats
      let responseMessage = response.data;
      if (typeof response.data === "object" && response.data.message) {
        responseMessage = response.data.message; // Handle JSON response like { "message": "updated successfully" }
      }

      // Normalize and check for success
      const normalizedResponse = responseMessage?.toString().trim().toLowerCase();
      if (normalizedResponse.includes("success")) {
        return response;
      } else {
        throw new Error(`Unexpected API response: ${JSON.stringify(response.data)}`);
      }
    } catch (error) {
      console.error(`Error updating product status (newStatus=${newStatus}):`, error);
      throw error; // Rethrow to handle in caller
    }
  };

  // Move products to active status (update state, track changes)
  const moveToActive = (products) => {
    const classIds = products.map((p) => p.id);
    setInactiveProducts(inactiveProducts.filter((p) => !classIds.includes(p.id)));
    setActiveProducts([
      ...activeProducts,
      ...products.map((p) => ({ ...p, active: true })),
    ]);
    setChangedProducts([
      ...changedProducts,
      ...products.map((p) => ({ id: p.id, newStatus: "Y" })),
    ]);
    toast({
      title: "Success",
      description: `${products.length} product(s) moved to active.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    gridRefInactive.current.api.deselectAll();
  };

  // Move products to inactive status (update state, track changes)
  const moveToInactive = (products) => {
    const classIds = products.map((p) => p.id);
    setActiveProducts(activeProducts.filter((p) => !classIds.includes(p.id)));
    setInactiveProducts([
      ...inactiveProducts,
      ...products.map((p) => ({ ...p, active: false })),
    ]);
    setChangedProducts([
      ...changedProducts,
      ...products.map((p) => ({ id: p.id, newStatus: "N" })),
    ]);
    toast({
      title: "Success",
      description: `${products.length} product(s) moved to inactive.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    gridRefActive.current.api.deselectAll();
  };

  // Handle Done button click to send changed products to API
  const handleDone = async () => {
    if (changedProducts.length === 0) {
      toast({
        title: "Info",
        description: "No changes to save.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Group changes by newStatus and sort IDs
      const activeIds = changedProducts
        .filter((p) => p.newStatus === "Y")
        .map((p) => p.id)
        .sort((a, b) => a - b); // Sort IDs in ascending order
      const inactiveIds = changedProducts
        .filter((p) => p.newStatus === "N")
        .map((p) => p.id)
        .sort((a, b) => a - b); // Sort IDs in ascending order

      console.log("Active IDs to update (sorted):", activeIds); // Debug log
      console.log("Inactive IDs to update (sorted):", inactiveIds); // Debug log

      let updatedCount = 0;

      // Update active products
      if (activeIds.length > 0) {
        await updateProductStatus(activeIds, "Y");
        updatedCount += activeIds.length;
      }

      // Update inactive products
      if (inactiveIds.length > 0) {
        await updateProductStatus(inactiveIds, "N");
        updatedCount += inactiveIds.length;
      }

      // Clear changed products after successful update
      setChangedProducts([]);
      toast({
        title: "Success",
        description: `${updatedCount} product(s) updated successfully.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to update product status: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Class ID",
        field: "id",
        sortable: true,
        filter: true,
        width: 80,
        headerCheckboxSelection: true,
        checkboxSelection: true,
      },
      { headerName: "Product Name", field: "name", sortable: true, filter: true },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      resizable: true,
    }),
    []
  );

  const handleMoveToActive = () => {
    const selectedRows = gridRefInactive.current.api.getSelectedRows();
    if (selectedRows.length > 0) {
      moveToActive(selectedRows);
    } else {
      toast({
        title: "Warning",
        description: "Please select at least one product to move.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMoveToInactive = () => {
    const selectedRows = gridRefActive.current.api.getSelectedRows();
    if (selectedRows.length > 0) {
      moveToInactive(selectedRows);
    } else {
      toast({
        title: "Warning",
        description: "Please select at least one product to move.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <style>{customAgGridStyles}</style>
      <Card mb="20px" p="20px">
        <Flex justify="flex-end" mb={4}>
          <Button colorScheme="blue" size="md" onClick={handleDone}>
            Done
          </Button>
        </Flex>
        <Flex align="center">
          <Box w="48%" mr={4}>
            <Text fontWeight="bold" mb={2}>
              Inactive Products
            </Text>
            <Box
              className="ag-theme-alpine custom-ag-grid"
              height="650px"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="lg"
              overflow="hidden"
            >
              <AgGridReact
                ref={gridRefInactive}
                rowData={inactiveProducts}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowHeight={50}
                headerHeight={60}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                rowSelection="multiple"
                suppressRowClickSelection={false}
                onGridReady={(params) => params.api.sizeColumnsToFit()}
              />
            </Box>
          </Box>
          <Flex direction="column" justify="center" align="center" w="4%">
            <Button mb={2} size="sm" onClick={handleMoveToActive}>
              {">>"}
            </Button>
            <Button size="sm" onClick={handleMoveToInactive}>
              {"<<"}
            </Button>
          </Flex>
          <Box w="48%">
            <Text fontWeight="bold" mb={2}>
              Active Products
            </Text>
            <Box
              className="ag-theme-alpine custom-ag-grid"
              height="650px"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="lg"
              overflow="hidden"
            >
              <AgGridReact
                ref={gridRefActive}
                rowData={activeProducts}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowHeight={50}
                headerHeight={60}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                rowSelection="multiple"
                suppressRowClickSelection={false}
                onGridReady={(params) => params.api.sizeColumnsToFit()}
              />
            </Box>
          </Box>
        </Flex>
      </Card>
    </Flex>
  );
};

export default ActiveProductScreen;